import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

interface User {
  id: string
  username: string
  email: string
  firstName?: string
  lastName?: string
  preferredName?: string
  avatar?: string
  mfa_enabled?: boolean
  email_verified?: boolean
  created_at?: string
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
}

const STORAGE_KEY = 'horton_auth'

function loadPersistedAuth(): AuthState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed.isAuthenticated && parsed.user) {
        return { ...parsed, isLoading: false }
      }
    }
  } catch { /* corrupt or unavailable */ }
  return { user: null, isAuthenticated: false, isLoading: false, token: null }
}

function persistAuth(state: AuthState) {
  try {
    if (state.isAuthenticated && state.user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        user: state.user,
        token: state.token,
        isAuthenticated: true,
      }))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch { /* localStorage unavailable */ }
}

const initialState: AuthState = loadPersistedAuth()

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user
      state.token = action.payload.token
      state.isAuthenticated = true
      state.isLoading = false
      persistAuth(state)
    },
    loginFailure: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      state.isLoading = false
      persistAuth(state)
    },
    logout: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      state.isLoading = false
      persistAuth(state)
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
        persistAuth(state)
      }
    },
  },
})

export const { setLoading, loginSuccess, loginFailure, logout, updateUser } = authSlice.actions